import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { FormWrapper } from 'Components/form';
import {
  mergeSchema,
  normalizeChoices,
  strBoolIsTruthy,
} from 'Components/form/utils';
import FNumber from 'Components/FNumber';
import dayjs from 'dayjs';
import FilesField from 'Components/form/FilesField';
import { api } from '~/api';
import { SelectField } from 'Components/form/fields';
import { FormattedMessage } from 'react-intl';
import { unitsOfMeasure, inArrayIf, getDecimalCount } from '~/utils';
import { Alert } from 'Components/nui';
import { Product } from '~/models';
import { useProducts } from '~/hooks/useProducts';

export const useFormWrapper = ({
  form,
  order,
  product,
  schema,
  solution,
  initialData,
  price: basePrice,
  displayedValue,
}) => {
  const exportIntent = strBoolIsTruthy(form, schema, 'exportable');

  const exportdocsRequired = strBoolIsTruthy(form, schema, 'exportdocs');

  const heatTreatedPalletsRequired = strBoolIsTruthy(
    form,
    schema,
    'heattreatedpallets'
  );

  const getChoicesByDivision =
    ({ choices }) =>
    () => {
      const divisionId = form.getFieldValue('division');
      return normalizeChoices(
        Array.isArray(choices) ? choices : R.propOr([], divisionId, choices)
      );
    };

  const totaldeliveries = R.propOr(1, 'totaldeliveries', order);

  const [files, setFiles] = useState([]);

  // Variant selection logic
  const variantId = form.getFieldValue('variant');
  const variantItem = order.variants?.find((v) => v.id === variantId);

  // Get full product details for selected variant
  const { data: products = [] } = useProducts();
  const storeVariantProduct = products.find(
    (p) => p.id === variantItem?.product.id
  );
  const variantProduct =
    variantItem?.product && storeVariantProduct
      ? {
          ...variantItem.product,
          ...storeVariantProduct,
        }
      : null;

  const variantLoading = variantItem?.loading ?? variantItem?.loadingObj;

  // Use variant loading if variant is selected, otherwise use order loading
  const loading = variantLoading ?? order.loading;

  // Use variant product if variant is selected, otherwise use the passed product
  // Need to create a new Product instance to have access to Product methods like _getValue
  const effectiveProduct = variantProduct
    ? new Product(variantProduct)
    : product;

  const step = R.prop('qty', loading);
  const volume = order.volume.delivery ?? order.volume.available;
  const totalVolume = volume * totaldeliveries;

  const [realPartyID, setRealPartyID] = useState();
  const [realPartyUserChoices, setRealPartyUserChoices] = useState([]);

  useEffect(() => {
    if (!realPartyID) {
      setRealPartyUserChoices([]);
    } else {
      (async () => {
        try {
          const data = await api.getData2(`/client-users/${realPartyID}`);
          setRealPartyUserChoices(R.propOr([], 'choices', data));
        } catch (error) {
          setRealPartyUserChoices([]);
        }
      })();
    }
  }, [realPartyID, setRealPartyUserChoices]);

  let fields = [
    {
      name: 'price',
      step: effectiveProduct.step,
      min: effectiveProduct.step,
    },
    {
      name: 'currency',
      choices: normalizeChoices,
    },
    {
      render: () => {
        if (!R.has('currency', schema)) return null;
        const { currency } = form.getFieldsValue();
        const cur = R.find(R.propEq('currency', currency))(order.currencies);
        return (
          <div key="currency-exchange" className="currency-exchange">
            {currency && (
              <>
                <span>
                  <FormattedMessage
                    id="marketrow-tradeform-field-currencyprice-label"
                    defaultMessage="Price for selected currency"
                  />
                </span>
                <strong>
                  <FNumber
                    value={R.pathOr(0, ['val'], cur)}
                    decimalCount={order.priceDecimalCount}
                  />{' '}
                  {R.pathOr(0, ['unit'], cur)}
                </strong>
              </>
            )}
          </div>
        );
      },
    },
    {
      name: 'division',
      choices: normalizeChoices,
    },
    {
      name: 'realparty',
      choices: normalizeChoices,
      props: {
        onChange: (x) => {
          setRealPartyID(x);
        },
      },
    },
  ];

  const realPartyUserField = new SelectField({
    name: 'realpartyuser',
    choices: () => normalizeChoices(realPartyUserChoices),
  });

  if (realPartyID) {
    fields.push({
      render: () => <>{realPartyUserField.render({ form })}</>,
      serializeField: (values) => ({
        realpartyuser: realPartyUserField.serialize(values.realpartyuser),
      }),
    });
  }

  // Add variant fields if order has variants
  if (schema.variant?.choices) {
    fields.push({
      name: 'variant',
      choices: (variant) =>
        variant?.choices?.map(({ id, name }) => ({
          value: id,
          label: name,
          key: id,
        })),
    });

    // Show loading details for selected variant
    fields.push({
      render: () => {
        // Check variant loading dynamically when rendering
        const currentVariantId = form.getFieldValue('variant');
        const currentVariantItem = order.variants?.find(
          (v) => v.id === currentVariantId
        );
        const currentVariantLoading =
          currentVariantItem?.loading ?? currentVariantItem?.loadingObj;

        if (!currentVariantLoading) return null;

        return (
          <div key="variant-loading">
            <strong>
              <FormattedMessage
                id="marketrow-fok-form-loading-details"
                defaultMessage="Loading details"
              />
            </strong>
            <div>{currentVariantLoading?.desc}</div>
            <hr className="mt-6" />
          </div>
        );
      },
    });
  }

  fields.push(
    {
      name: 'volume',
      label: (
        <>
          {/* {schema?.volume?.label}{' '} TEMP FE override */}
          <FormattedMessage
            id="marketrow-tradeform-field-volume-label"
            defaultMessage="Volume"
          />{' '}
          {!!order.totaldeliveries && (
            <FormattedMessage id="per-delivery" defaultMessage="per delivery" />
          )}
        </>
      ),
      step,
      min: () => (order.splittable ? Number(step) : volume),
      max: volume,
      props: () => ({ disabled: !order.splittable }),
    },
    {
      render: () => {
        const requiredFields = Object.keys(schema).filter(
          (key) => schema[key]?.required
        );
        const hasAllRequired = requiredFields.every((field) => {
          const value = form.getFieldValue(field);
          return value !== undefined && value !== null && value !== '';
        });

        const currency = form.getFieldValue('currency') || order.price.currency;
        const formVolume = form.getFieldValue('volume');
        let price = form.getFieldValue('price');
        if (!price) {
          if (currency === order.price.currency) price = basePrice;
          else price = order.getPriceObjByCur(currency).val;
        }

        const volume = formVolume || order.volume.pending;
        const unit = R.path(['volume', 'unit'], order);

        if (order.index || !hasAllRequired) return null;

        return (
          <div key="fok-value" className="currency-exchange">
            <span className="label w-auto pr-10 inline-block nowrap">
              <strong className="all-black">
                <FormattedMessage
                  id="marketrow-tradeform-field-totalvalue-label"
                  defaultMessage="Total value:"
                />
              </strong>
              <FNumber
                value={
                  formVolume
                    ? (displayedValue ??
                      (effectiveProduct?.currency
                        ? effectiveProduct._getValue(
                            price,
                            volume * totaldeliveries,
                            loading.id
                          )
                        : 0))
                    : 0
                }
                decimalCount={2}
              />{' '}
              {currency}
            </span>

            {order.totaldeliveries && (
              <span className="label w-auto pr-10 inline-block nowrap">
                <strong className="all-black">
                  <FormattedMessage
                    id="marketrow-tradeform-field-totalvolume-label"
                    defaultMessage="Total volume:"
                  />
                </strong>
                <FNumber
                  value={volume * totaldeliveries}
                  decimalCount={order.priceDecimalCount}
                />{' '}
                {R.pathOr(unit, [unit], unitsOfMeasure)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      name: 'files',
      render: () => {
        if (!R.prop('files', schema)) return null;
        return (
          <FilesField
            key="form-files"
            initialData={initialData}
            accept={R.pathOr([], ['files', 'accepted'], schema).join(', ')}
            files={files}
            setFiles={setFiles}
          />
        );
      },
      serializeField: () => {
        if (!R.prop('files', schema)) return null;
        return files.length ? { files } : undefined;
      },
    },
    {
      name: 'attachments',
      choices: normalizeChoices,
      initialValue: R.pathOr([], ['attachments', 'value'], schema),
      initialData: R.pathOr([], ['attachments', 'value'], schema),
    },
    {
      name: 'fromaddr',
      choices: getChoicesByDivision,
    },
    {
      name: 'toaddr',
      choices: getChoicesByDivision,
    },
    {
      name: 'warehouse',
      choices: getChoicesByDivision,
      props: {
        placeholder: (
          <FormattedMessage
            id="marketrow-tradeform-field-deliveryfrom-placholder"
            defaultMessage="Select location"
          />
        ),
      },
    },
    {
      name: 'buyer',
      choices: normalizeChoices,
    },
    {
      name: 'consignee',
      choices: normalizeChoices,
    },
    {
      name: 'exportable',
      initialValue: schema?.exportable?.value,
      // required to displays properly
      required: true,
    },

    ...inArrayIf(
      !('exportable' in schema) || (exportIntent && order.type === 'offer'),
      {
        name: 'exportdocs',
        initialValue: schema?.exportdocs?.value,
        // required to displays properly
        required: true,
        choices: [
          {
            label: 'Yes',
            value: 'true',
            key: 'yes',
          },
          { label: 'Not required', value: 'false', key: 'no' },
        ],
      }
    ),
    {
      render: () =>
        exportIntent &&
        solution?.exportprice?.flatfee &&
        exportdocsRequired && (
          <Alert
            hasicon
            type="info"
            className="alert-fee"
            key="alert-exportfee"
          >
            Please note a{' '}
            <strong>
              {solution.exportprice.price} {solution.currency}
            </strong>{' '}
            flat fee will be added to this order.{' '}
            {solution.exportprice.file && (
              <a
                href={solution.exportprice.file.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                View more information here
              </a>
            )}
          </Alert>
        ),
    },

    ...inArrayIf(exportIntent, {
      name: 'heattreatedpallets',
      choices: [
        {
          label: 'Yes',
          value: 'true',
          key: 'yes',
        },
        { label: 'Not required', value: 'false', key: 'no' },
      ],
      initialValue: schema?.heattreatedpallets?.value,
    }),

    {
      render: () =>
        exportIntent &&
        heatTreatedPalletsRequired &&
        totalVolume > 0 &&
        solution?.exportprice?.heattreatedfee && (
          <Alert
            hasicon
            type="info"
            className="pallets-request"
            key="pallets-request"
          >
            <h3>Heat-treated pallets summary</h3>
            <div>
              <blockquote>
                <FNumber
                  value={parseFloat(solution.exportprice.heattreatedfee)}
                  decimalCount={getDecimalCount(
                    parseFloat(solution.exportprice.heattreatedfee)
                  )}
                  unit={solution.currency}
                />{' '}
                &times;{' '}
                <FNumber
                  value={totalVolume}
                  decimalCount={getDecimalCount(totalVolume)}
                />{' '}
                ={' '}
                <FNumber
                  value={
                    parseFloat(solution.exportprice.heattreatedfee) *
                    totalVolume
                  }
                  decimalCount={2}
                  unit={solution.currency}
                />
              </blockquote>
            </div>
          </Alert>
        ),
    },

    ...inArrayIf(exportIntent, {
      name: 'exportcountry',
      choices: normalizeChoices(schema?.exportcountry?.choices),
      initialValue: schema?.exportcountry?.value,
    }),

    {
      name: 'callofftime',
      required: () => {
        if (!order.etd.from || !order.etd.to) return false;
        return dayjs(order.etd.from).add(28, 'days') <= dayjs(order.etd.to);
      },
      initialValue: R.pathOr('', ['callofftime', 'value'], schema),
      initialData: R.pathOr('', ['callofftime', 'value'], schema),
    },
    {
      name: 'reference',
      // TEMP FE override
      label: (
        <FormattedMessage
          id="marketrow-tradeform-field-reference-label"
          defaultMessage="Reference"
        />
      ),
    },
    {
      name: 'note',
    }
  );

  let init = initialData || {};
  if (!('volume' in init)) init.volume = order.volume.available;

  return new FormWrapper(form, init, mergeSchema(schema, fields));
};
